import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Users, Target, Percent } from 'lucide-react';

interface NewsletterGoal {
    newsletterName: string;
    quarterGoal: number;
    initialValue: number;
}

interface NewsletterStats {
    name: string;
    active: number;
}

interface NewsletterOverviewProps {
    metrics: Array<{ name: string; active: number }>;
    period: {
        startDate: string;
        endDate: string;
    };
    goals: Record<string, NewsletterGoal>;
    stats: NewsletterStats[];
    summary: {
        newsletters: Array<{
            caderno: string;
            newsletterName: string;
            yesterdayTotal: number;
            yesterdayIncrease: number;
            currentTotal: number;
            periodIncrease: number;
            goal: number;
            goalProgress: number;
        }>;
        period: {
            startDate: string;
            endDate: string;
        };
    };
}

const NewsletterOverview: React.FC<NewsletterOverviewProps> = ({ metrics, period, goals, stats, summary }) => {
    const formatNumber = (num: number): string =>
        new Intl.NumberFormat('pt-BR').format(num || 0);

    const calculateRemainingDays = () => {
        const end = new Date(period.endDate);
        const today = new Date();
        const diffTime = Math.abs(end.getTime() - today.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getNewsletterData = (newsletterId: string) => {
        const metric = metrics.find(m => m.name === newsletterId);
        const goal = goals[newsletterId];

        if (!metric || !goal) return {
            totalSubscribers: 0,
            periodGrowth: 0,
            goalProgress: 0,
            remainingDays: calculateRemainingDays(),
            quarterGoal: 0,
            newsletterName: ''
        };

        const currentValue = metric.active;
        const totalSubscribers = currentValue + goal.initialValue;
        const periodGrowth = currentValue;

        const targetTotal = goal.quarterGoal;
        const goalProgress = (totalSubscribers / targetTotal) * 100;

        return {
            totalSubscribers,
            periodGrowth,
            goalProgress,
            remainingDays: calculateRemainingDays(),
            quarterGoal: goal.quarterGoal,
            newsletterName: goal.newsletterName,
            initialValue: goal.initialValue || 0,
            targetTotal: goal.quarterGoal || 0
        };
    };

    const newsletters = useMemo(() => [
        { id: 'thenews', icon: '/thenews.webp' },
        { id: 'thechamps', icon: '/thechamps.webp' },
        { id: 'thestories', icon: '/thestories.webp' },
        { id: 'goget', icon: '/goget.webp' },
        { id: 'rising', icon: '/rising.webp' },
        { id: 'thebizness', icon: '/thebizness.webp' }
    ], []);

    const NewsletterCard: React.FC<{ newsletter: { id: string; icon: string } }> = React.memo(({ newsletter }) => {
        const data = getNewsletterData(newsletter.id);
        const currentActive = stats.find(stat => stat.name === newsletter.id)?.active || 0;

        // Pegando os dados específicos do summary para cada newsletter
        const summaryData = summary.newsletters.find(n => n.caderno === newsletter.id);

        return (
            <Card className="overflow-hidden dark:bg-gray-800">
                <CardHeader className="border-b bg-gray-50 dark:bg-gray-900 p-4">
                    <div className="flex items-center gap-3">
                        <img
                            src={newsletter.icon}
                            alt={data.newsletterName}
                            className="h-6 w-auto"
                            loading="lazy"
                        />
                        <div className="flex flex-col text-xs text-gray-600 dark:text-gray-400">
                            <span>
                                Total ontem: {formatNumber(summaryData?.yesterdayTotal || 0)}
                            </span>
                            <span>
                                Aumento ontem: {formatNumber(summaryData?.yesterdayIncrease || 0)}
                            </span>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="space-y-4">
                        <MetricRow
                            icon={<Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                            label="Total de Inscritos"
                            value={formatNumber(currentActive)}
                        />

                        <MetricRow
                            icon={<TrendingUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                            label="Crescimento"
                            value={formatNumber(data.periodGrowth + (data.initialValue || 0))}
                        />

                        <MetricRow
                            icon={<Target className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                            label="Meta do Semestre"
                            value={formatNumber(summaryData?.goal || data.quarterGoal)}
                        />

                        <MetricRow
                            icon={<Percent className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                            label="Progresso da Meta"
                            value={`${data.goalProgress.toFixed(1)}%`}
                        />

                        <ProgressBar progress={data.goalProgress} />

                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <MetricBox
                                label="Dias Restantes"
                                value={data.remainingDays.toString()}
                            />
                            <MetricBox
                                label="Faltam"
                                value={formatNumber(data.quarterGoal - data.totalSubscribers)}
                            />
                        </div>

                    </div>
                </CardContent>
            </Card>
        );
    });

    // Componentes reutilizáveis
    const MetricRow: React.FC<{
        icon: React.ReactNode;
        label: string;
        value: string;
        comparison?: {
            value: number;
            label: string;
        };
    }> = React.memo(({ icon, label, value, comparison }) => (
        <div className="space-y-1">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    {icon}
                    <span className="text-sm text-gray-400 dark:text-gray-100">{label}</span>
                </div>
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</span>
            </div>
            {comparison && (
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-400 justify-end">
                    <span>{comparison.label}</span>
                    {comparison.value > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                    )}
                    <span>{formatNumber(Math.abs(comparison.value))}</span>
                </div>
            )}
        </div>
    ));

    const ProgressBar: React.FC<{ progress: number }> = React.memo(({ progress }) => (
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <div
                className="bg-[#FF3366] h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, progress)}%` }}
            />
        </div>
    ));

    const MetricBox: React.FC<{ label: string; value: string }> = React.memo(({ label, value }) => (
        <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-sm text-gray-400 dark:text-gray-100">{label}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">{value}</div>
        </div>
    ));

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {newsletters.map((newsletter) => (
                <NewsletterCard
                    key={newsletter.id}
                    newsletter={newsletter}
                />
            ))}
        </div>
    );
};

export default React.memo(NewsletterOverview);